import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import {
  user,
  useGetOfficeDeliveryListDataQuery,
  useUpdateOfficeDeliveryHeadDataMutation,
  useUpdateOfficeDeliveryStatusMutation,
} from "../../../store/queries/User";
import { deleteOfficeDelivery } from "./api";
import { toast } from "react-toastify";
import { current } from "@reduxjs/toolkit";
import { getOfficeDeliveryEditData, updateConfig } from "../../../store/slices/User/OfficeDelivery/officeDeliverySlice";

const useOfficeDelivery = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
 
  const dispatch = useDispatch();
  const officeDeliveryState = useSelector((state) => state.officeDelivery);
  const { selectedId } = useSelector((state) => state.officeDelivery);
  const { showEditModal } = useSelector((state) => state.global);
  const {
    data: officeDeliveryList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetOfficeDeliveryListDataQuery({
    page_size: officeDeliveryState?.currentPageSize,
    page: officeDeliveryState?.currentPage,
    sort: officeDeliveryState?.sortBy,
    sort_order: officeDeliveryState?.sortOrder,
    search: officeDeliveryState?.search,
  });
  const [updateOfficeDeliveryFields] = useUpdateOfficeDeliveryHeadDataMutation();
  const [updateOfficeDeliveryStatus] = useUpdateOfficeDeliveryStatusMutation();
  const handleCreateClick = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = !state.showCreateModal;
        state.imagePreview = "";
        state.imageName = "";
        state.arabicImagePreview = "";
        state.arabicImageName = "";
        state.isEdit = false;
        state.editOfficeDeliveryData = {};
      })
    );
  };
  useEffect(() => {
    if (officeDeliveryState.clearSelection) {
      dispatch(
        updateConfig((state) => {
          state.clearSelection = false;
        })
      );
    }
    // eslint-disable-next-line
  }, [officeDeliveryState.clearSelection]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };
  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleToggle = (e, data) => {
    const newStatus = data.status === 1 ? 2 : 1;
    let body = {
      id: data?._id,
      status: newStatus,
    };
    updateOfficeDeliveryStatus(body);
    dispatch(
      user.util.updateQueryData(
        "getOfficeDeliveryListData",
        {
          page_size: officeDeliveryState?.currentPageSize,
          page: officeDeliveryState?.currentPage,
          sort: officeDeliveryState?.sortBy,
          sort_order: officeDeliveryState?.sortOrder,
          search: officeDeliveryState?.search,
        },
        (cacheEntry) => {
          let currentCache = { ...current(cacheEntry) };
          const newResult = currentCache.data.result?.map((res) =>
            res._id === data?._id
              ? {
                  ...res,
                  status: newStatus,
                }
              : res
          );
          currentCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              result: newResult,
            },
          };
          return currentCache;
        }
      )
    );
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
      status: (field, data) => {
        const isChecked = data?.status === 1;
        return (
          <div className="form-check form-switch ">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isChecked}
              onChange={(e) => handleToggle(e, data)}
            />
          </div>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handleSort = (label) => {
    if (officeDeliveryState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = officeDeliveryState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleDeleteModal = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showDeleteModal = !state.showDeleteModal;
        state.selectedId = e?.[0];
      })
    );
  };

  const handleDelete = async () => {
    const response = await deleteOfficeDelivery(selectedId);
    if (response && response.status === 200) {
      toast.success(`Office Delivery deleted successfully.`);
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.search="";
        })
      );
      setTimeout(() => {
        refetch();
      }, 0);
    } else {
      toast.error(`Something went wrong !`);
    }
    handleDeleteModal();
    dispatch(
      updateConfig((state) => {
        state.clearSelection = true;
      })
    );
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };
  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };
 
  const handleEditAction = (e) => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = true;
        state.selectedId = e?.[0];
        state.isEdit = true;
      })
    );
    dispatch(getOfficeDeliveryEditData(e?.[0]));
  };

  return {
    isLoading,
    isFetching,
    officeDeliveryList,
    showEditModal,
    officeDeliveryState,
    paginationOptions,
    handleEditColumnsClick,
    updateOfficeDeliveryFields,
    handleDeleteModal,
    handleCreateClick,
    handlePagination,
    handleClearClick,
    handleEditAction,
    handlePageSize,
    handleSearch,
    handleSort,
    handleDelete,
    refetch,
    getRow,
  };
};

export default useOfficeDelivery;
